'use client'

import { ReactNode, CSSProperties, useEffect, useRef } from 'react'
import { cn } from '@/utilities/ui'

export interface FadeInProps {
  children: ReactNode
  className?: string
  style?: CSSProperties
  initialY?: number
  duration?: number
  delay?: number
  once?: boolean
  viewportMargin?: string
}

// Shared module-level observers keyed by rootMargin so many FadeIn instances
// reuse a single IntersectionObserver instead of spawning one each.
const observers = new Map<string, IntersectionObserver>()
const elementOptions = new WeakMap<Element, { delay: number; once: boolean }>()

function getObserver(rootMargin: string): IntersectionObserver {
  const existing = observers.get(rootMargin)
  if (existing) return existing
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const opts = elementOptions.get(entry.target)
        if (!opts) continue
        const el = entry.target as HTMLElement
        if (entry.isIntersecting) {
          if (opts.delay > 0) {
            window.setTimeout(() => {
              el.dataset.animate = 'visible'
            }, opts.delay * 1000)
          } else {
            el.dataset.animate = 'visible'
          }
          if (opts.once) observer.unobserve(el)
        } else if (!opts.once) {
          el.dataset.animate = 'hidden'
        }
      }
    },
    // threshold: 0 fires on any intersection. 0.1 (10%) failed for
    // elements taller than ~10× viewport — the max possible ratio never
    // reaches 10%, leaving data-animate="hidden" and the section invisible.
    { rootMargin, threshold: 0 },
  )
  observers.set(rootMargin, observer)
  return observer
}

export default function FadeIn({
  children,
  className = '',
  style,
  initialY = 30,
  duration = 0.8,
  delay = 0,
  once = true,
  viewportMargin = '-100px',
}: FadeInProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    elementOptions.set(el, { delay, once })
    const observer = getObserver(viewportMargin)
    observer.observe(el)
    return () => {
      observer.unobserve(el)
      elementOptions.delete(el)
    }
  }, [delay, once, viewportMargin])

  const dynamicStyle: CSSProperties = {
    ...style,
    '--initial-y': `${initialY}px`,
    animationDelay: `${delay}s`,
    animationDuration: `${duration}s`,
  } as CSSProperties

  return (
    <div
      ref={ref}
      data-animate="hidden"
      className={cn(
        className,
        'opacity-0',
        initialY !== 0
          ? 'data-[animate=visible]:animate-fade-in-up'
          : 'data-[animate=visible]:animate-fade-in',
      )}
      style={dynamicStyle}
    >
      {children}
    </div>
  )
}
